/* Shared, lifecycle-aware interactions for the product story sections. */
(() => {
  if (customElements.get('layout-carousel')) return;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LayoutCarousel extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.allSlides = [...this.track.children];
      this.slides = this.allSlides;
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this.index = 0;
      if (this.hasAttribute('data-inline-video-autoplay')) this.initInlineVideos(options);
      if (this.hasAttribute('data-review-tabs')) this.initReviewTabs(options);
      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.go(this.index - 1), options);
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.go(this.index + 1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index), options));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const selected = this.allSlides.find(slide => slide.contains(event.target));
        if (selected && this.hasAttribute('data-review-tabs')) this.selectReviewGroup(selected.dataset.reviewGroup);
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0) this.go(index, true);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }
    initInlineVideos(options) {
      this.inlineVideos = [...this.querySelectorAll('[data-inline-video] video')];
      this.activeInlineVideos = new Set();
      this.inlineVideos.forEach(video => {
        video.addEventListener('play', () => {
          // Allow visible desktop videos to play together, including native-control playback.
          if (!this.visibleInlineVideos().includes(video)) {
            video.pause();
            return;
          }
          video.closest('[data-inline-video]').classList.add('is-playing');
        }, options);
        video.addEventListener('pause', () => video.closest('[data-inline-video]').classList.remove('is-playing'), options);
        video.addEventListener('ended', () => video.closest('[data-inline-video]').classList.remove('is-playing'), options);
      });
    }
    visibleInlineVideos() {
      if (window.matchMedia('(max-width: 768px)').matches) {
        const video = this.slides[this.index]?.querySelector('[data-inline-video] video');
        return video ? [video] : [];
      }
      const trackBox = this.track.getBoundingClientRect();
      return this.slides.map(slide => slide.querySelector('[data-inline-video] video')).filter(video => {
        if (!video) return false;
        const box = video.getBoundingClientRect();
        const visibleWidth = Math.min(box.right, trackBox.right) - Math.max(box.left, trackBox.left);
        // Ignore cards that only peek into the track while scrolling.
        return box.width > 0 && visibleWidth > box.width / 2;
      }).slice(0, 2);
    }
    updateInlineVideo() {
      if (!this.inlineVideos) return;
      const previous = this.activeInlineVideos;
      this.activeInlineVideos = new Set(this.visibleInlineVideos());
      this.inlineVideos.forEach(video => {
        if (!this.activeInlineVideos.has(video)) video.pause();
      });
      this.activeInlineVideos.forEach(video => {
        // Preserve manual pauses and avoid restarting videos that remain visible.
        if (previous.has(video)) return;
        video.muted = true;
        // Keep native controls available if the browser rejects autoplay.
        video.play().catch(() => {});
      });
    }
    initReviewTabs(options) {
      const tablist = this.querySelector('[data-review-tablist]');
      if (!tablist || !this.allSlides.length) return;
      const groups = [...new Set(this.allSlides.map(slide => slide.dataset.reviewGroup))];
      tablist.replaceChildren();
      this.reviewTabs = groups.map((group, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'testimonial-video-carousel__tab';
        button.id = `${this.track.id}-tab-${index}`;
        button.textContent = group;
        button.dataset.reviewGroup = group;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', this.track.id);
        button.addEventListener('click', () => this.selectReviewGroup(group), options);
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? groups.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + groups.length) % groups.length;
          this.selectReviewGroup(groups[next]);
          this.reviewTabs[next].focus();
        }, options);
        tablist.append(button);
        return button;
      });
      this.track.setAttribute('role', 'tabpanel');
      this.querySelector('[data-review-toolbar]').hidden = false;
      this.selectReviewGroup(groups[0]);
    }
    selectReviewGroup(group) {
      if (!this.reviewTabs?.length) return;
      this.reviewTabs.forEach(tab => {
        const active = tab.dataset.reviewGroup === group;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) this.track.setAttribute('aria-labelledby', tab.id);
      });
      this.allSlides.forEach(slide => {
        slide.hidden = slide.dataset.reviewGroup !== group;
        if (slide.hidden) {
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        }
      });
      this.slides = this.allSlides.filter(slide => !slide.hidden);
      this.index = 0;
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
    }
    go(index, instant = false) {
      if (!this.slides.length) return;
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      const trackBox = this.track.getBoundingClientRect();
      const slideBox = this.slides[index].getBoundingClientRect();
      this.track.scrollTo({ left: this.track.scrollLeft + slideBox.left - trackBox.left, behavior: instant || reducedMotion() ? 'instant' : 'smooth' });
    }
    update() {
      if (!this.track.clientWidth || !this.slides.length) return;
      const box = this.track.getBoundingClientRect();
      this.index = this.slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - box.left) < Math.abs(this.slides[best].getBoundingClientRect().left - box.left) ? index : best, 0);
      const atStart = Math.abs(this.track.scrollLeft) < 2;
      const atEnd = Math.abs(this.track.scrollLeft) + this.track.clientWidth >= this.track.scrollWidth - 2;
      this.querySelector('[data-carousel-prev]')?.toggleAttribute('disabled', atStart);
      this.querySelector('[data-carousel-next]')?.toggleAttribute('disabled', atEnd);
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      this.updateInlineVideo();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.inlineVideos?.forEach(video => video.pause());
      this.activeInlineVideos?.clear();
    }
  }

  class LayoutTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        }, options);
      });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.panels.findIndex(panel => panel.contains(event.target));
        if (index >= 0) this.select(index);
      }, options);
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(index === i));
        tab.tabIndex = index === i ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = index !== i; });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  const modalLocks = new Set();
  let originalOverflow;
  class LayoutModal extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.querySelectorAll('[data-modal-open]').forEach(button => button.addEventListener('click', () => this.open(button), options));
      this.querySelector('[data-modal-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.cleanup(), options);
      this.dialog.addEventListener('keydown', event => {
        if (this.hasAttribute('data-inline')) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.dialog.close();
          }
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
    }
    open(button) {
      this.opener = button;
      // Show first so mobile media visibility can be determined accurately.
      if (this.hasAttribute('data-inline')) {
        this.classList.add('is-open');
        this.querySelector('.video-overlay-panel__hero').inert = true;
        this.dialog.show();
      } else {
        this.dialog.showModal();
      }
      this.dialog.querySelectorAll('template[data-modal-video]').forEach(template => {
        if (!template.parentElement.getClientRects().length) return;
        if (!template.parentElement.querySelector('video')) template.parentElement.append(template.content.cloneNode(true));
      });
      if (!this.hasAttribute('data-inline')) {
        if (!modalLocks.size) originalOverflow = document.body.style.overflow;
        modalLocks.add(this);
        document.body.style.overflow = 'hidden';
      }
      button.setAttribute('aria-expanded', 'true');
      this.querySelectorAll('[data-background-video] video').forEach(video => video.pause());
      const video = this.dialog.querySelector('video');
      if (video && video.getClientRects().length) video.play().catch(() => {});
      this.querySelector('[data-modal-close]')?.focus({ preventScroll: true });
    }
    cleanup() {
      this.classList.remove('is-open');
      const hero = this.querySelector('.video-overlay-panel__hero');
      if (hero) hero.inert = false;
      this.dialog.querySelectorAll('video').forEach(video => video.pause());
      this.opener?.setAttribute('aria-expanded', 'false');
      if (modalLocks.delete(this) && !modalLocks.size) document.body.style.overflow = originalOverflow;
      if (this.isConnected) {
        this.opener?.focus({ preventScroll: true });
        if (!reducedMotion()) this.querySelectorAll('[data-background-video]').forEach(player => player.resume?.());
      }
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      if (this.dialog) this.cleanup();
      this.controller?.abort();
    }
  }

  class LayoutVideo extends HTMLElement {
    connectedCallback() {
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.resume();
        } else this.querySelector('video')?.pause();
      }), { threshold: 0.15 });
      this.observer.observe(this);
    }
    resume() {
      if (reducedMotion() || this.closest('layout-modal')?.querySelector('dialog[open]')) return;
      if (!this.querySelector('video')) {
        const template = this.querySelector('template');
        if (template) this.append(template.content.cloneNode(true));
      }
      const video = this.querySelector('video');
      if (video) video.play().catch(() => {});
    }
    disconnectedCallback() { this.observer?.disconnect(); this.querySelector('video')?.pause(); }
  }
  class LayoutComparison extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const track = this.querySelector('[data-table-scroll]');
      const prev = this.querySelector('[data-table-prev]');
      const next = this.querySelector('[data-table-next]');
      const update = () => {
        prev.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.7, behavior: reducedMotion() ? 'instant' : 'smooth' }), options);
      next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.7, behavior: reducedMotion() ? 'instant' : 'smooth' }), options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(track);
      update();
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); }
  }
  customElements.define('layout-comparison', LayoutComparison);
  customElements.define('layout-carousel', LayoutCarousel);
  customElements.define('layout-tabs', LayoutTabs);
  customElements.define('layout-modal', LayoutModal);
  customElements.define('layout-video', LayoutVideo);
})();
